import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { fetchData } from '../../helpers/apiUtils';
import { NfaSendRecord } from '../../helpers/nfaMailingData';

const SendHistory: FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sends, setSends] = useState<NfaSendRecord[]>([]);
  const [selectedSend, setSelectedSend] = useState<NfaSendRecord | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const result = await fetchData<NfaSendRecord[]>('/api/nfa-mailings/sends?limit=50');
      if (cancelled) return;
      if (result.success && result.data) {
        setSends(result.data);
      } else {
        setError(result.error || 'Fehler beim Laden');
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Chip label="Gesendet" size="small" color="success" />;
      case 'prepared':
        return <Chip label="Vorbereitet" size="small" color="warning" />;
      case 'cancelled':
        return <Chip label="Abgebrochen" size="small" color="default" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getSourceTypeChip = (sourceType: NfaSendRecord['source_type']) => {
    switch (sourceType) {
      case 'webinar':
        return <Chip label="Webinar" size="small" color="primary" />;
      case 'kme':
        return <Chip label="KME" size="small" color="info" />;
      case 'vnr':
        return <Chip label="VNR" size="small" color="warning" />;
      case 'plx':
        return <Chip label="PLX" size="small" color="secondary" />;
      default:
        return <Chip label={sourceType} size="small" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (sends.length === 0) {
    return <Alert severity="info">Noch keine NFA-Mailings versendet.</Alert>;
  }

  return (
    <Box>
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  color: 'black',
                  fontWeight: 'bold',
                }}
              >
                Datum
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  color: 'black',
                  fontWeight: 'bold',
                }}
              >
                Betreff
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  color: 'black',
                  fontWeight: 'bold',
                }}
              >
                Typ
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  color: 'black',
                  fontWeight: 'bold',
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  color: 'black',
                  fontWeight: 'bold',
                }}
              >
                ANFOs
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  color: 'black',
                  fontWeight: 'bold',
                }}
              >
                Registrierungen
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sends.map((send) => (
              <TableRow
                key={send.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={async () => {
                  const detail = await fetchData<NfaSendRecord>(
                    `/api/nfa-mailings/sends/${send.id}`,
                  );
                  if (detail.success && detail.data) {
                    setSelectedSend(detail.data);
                  }
                }}
              >
                <TableCell>
                  {new Date(send.planned_send_date).toLocaleDateString('de-DE')}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                    {send.subject}
                  </Typography>
                </TableCell>
                <TableCell>{getSourceTypeChip(send.source_type)}</TableCell>
                <TableCell>{getStatusChip(send.status)}</TableCell>
                <TableCell>{send.order_count || 0}</TableCell>
                <TableCell>{send.registration_count || 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail dialog */}
      <Dialog open={!!selectedSend} onClose={() => setSelectedSend(null)} maxWidth="lg" fullWidth>
        {selectedSend && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{selectedSend.subject}</Typography>
                <IconButton onClick={() => setSelectedSend(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box display="flex" gap={2} mb={2}>
                {getSourceTypeChip(selectedSend.source_type)}
                {getStatusChip(selectedSend.status)}
                <Typography variant="body2" color="textSecondary">
                  Versanddatum:{' '}
                  {new Date(selectedSend.planned_send_date).toLocaleDateString('de-DE')}
                </Typography>
                {selectedSend.from_name && (
                  <Typography variant="body2" color="textSecondary">
                    Von: {selectedSend.from_name}
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary">
                  Tracking: {selectedSend.nfa_send_code}
                </Typography>
              </Box>

              {selectedSend.status === 'confirmed' && (
                <Box display="flex" gap={3} mb={2}>
                  <Typography variant="body2">
                    <strong>ANFOs:</strong> {selectedSend.order_count}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Registrierungen:</strong> {selectedSend.registration_count}
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                  mt: 2,
                }}
              >
                <iframe
                  title="Send Preview"
                  srcDoc={selectedSend.final_html}
                  style={{
                    width: '100%',
                    height: '600px',
                    border: 'none',
                  }}
                />
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SendHistory;
